import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Box,
  Divider,
  IconButton,
  Grid,
  CircularProgress,
  Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachmentIcon from '@mui/icons-material/Attachment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  useAdditionalDocumentsQuery,
  useAdditionalDocumentsUpload,
} from '../../../rest/useCompanyInfo';
import ModalsWrapper from '../../../Components/Modals/ModalsWrapper';
import PdfAndImageView from '../../../Components/pdfAndImageView/PdfAndImageView';
import moment from 'moment';
import { imgUrl } from '../../../Helpers/utils/Constant';
import { useAuthContext } from '../../../Context/AuthContext/AuthContext';

const AdditionalFileUpload = () => {
  const { user } = useAuthContext();
  const { additionalDocuments, isLoading: isFetching } = useAdditionalDocumentsQuery();
  const { mutate: uploadDocs, isLoading: isUploading } = useAdditionalDocumentsUpload();

  const [toggleUpload, setToggleUpload] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const handleRemoveSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('file', file);
    });
    uploadDocs(formData, {
      onSuccess: () => {
        setSelectedFiles([]);
        setToggleUpload(false);
      },
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const filesArray = Array.from(e.dataTransfer.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const getFileName = (path: string) => {
    return path.split('/').pop() || 'document';
  };

  return (
    <Card sx={{ mt: 1, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', borderRadius: '12px', overflow: 'hidden' }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <AttachmentIcon color="primary" sx={{ fontSize: 22 }} />
            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: '1.05rem', color: '#1e293b' }}>
              Additional Documents
            </Typography>
          </Box>
        }
        action={
          user?.user_member_account_status?.toLowerCase() === 'shortfall' && (
            <Button
              variant={toggleUpload ? 'outlined' : 'contained'}
              color={toggleUpload ? 'error' : 'primary'}
              onClick={() => {
                setToggleUpload(!toggleUpload);
                if (toggleUpload) {
                  setSelectedFiles([]);
                }
              }}
              startIcon={toggleUpload ? null : <CloudUploadIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                px: 2.5,
                py: 0.5,
                fontSize: '0.825rem',
                fontWeight: 'bold',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {toggleUpload ? 'Cancel' : 'Upload Files'}
            </Button>
          )
        }
        sx={{ py: 1.5, px: 2 }}
      />
      <Divider />
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {toggleUpload ? (
          // Upload interface - shown when toggleUpload is active
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 2.5,
                border: isDragging ? '2px dashed #4f46e5' : '2px dashed #cbd5e1',
                borderRadius: '12px',
                backgroundColor: isDragging ? '#f5f7ff' : '#f8fafc',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  borderColor: '#4f46e5',
                  backgroundColor: '#f1f5f9',
                },
              }}
              component="label"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="additional-files"
              />
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  backgroundColor: isDragging ? '#e0e7ff' : '#e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  color: isDragging ? '#4f46e5' : '#475569',
                  transition: 'all 0.3s',
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 22 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25, color: '#0f172a', fontSize: '0.9rem' }}>
                Drag & drop files here, or <span style={{ color: '#4f46e5', textDecoration: 'underline' }}>browse</span>
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                PDF, PNG, JPG, or JPEG (Max 5MB each)
              </Typography>
            </Box>

            {/* Selected files preview */}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#0f172a', fontSize: '0.85rem' }}>
                  Selected Files ({selectedFiles.length})
                </Typography>
                <Grid container spacing={1.5}>
                  {selectedFiles.map((file, idx) => {
                    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
                    const isImage = file.type.startsWith('image/') ||
                      file.name.toLowerCase().endsWith('.png') ||
                      file.name.toLowerCase().endsWith('.jpg') ||
                      file.name.toLowerCase().endsWith('.jpeg');

                    return (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderColor: '#e2e8f0',
                            backgroundColor: '#ffffff',
                            position: 'relative',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#cbd5e1',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '6px',
                              backgroundColor: isPdf ? '#fef2f2' : isImage ? '#f0fdf4' : '#f8fafc',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: isPdf ? '#ef4444' : isImage ? '#22c55e' : '#64748b',
                              flexShrink: 0,
                            }}
                          >
                            {isPdf ? (
                              <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                            ) : isImage ? (
                              <ImageIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <InsertDriveFileIcon sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', display: 'block' }} noWrap title={file.name}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block' }}>
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              handleRemoveSelectedFile(idx);
                            }}
                            sx={{
                              p: 0.5,
                              backgroundColor: '#fef2f2',
                              color: '#ef4444',
                              '&:hover': {
                                backgroundColor: '#fee2e2',
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box display="flex" justifyContent="flex-end" gap={1.5} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => setSelectedFiles([])}
                    sx={{ borderRadius: '8px', px: 2.5, py: 0.5, textTransform: 'none', fontSize: '0.8rem', fontWeight: 'bold' }}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleUpload}
                    disabled={isUploading}
                    startIcon={isUploading ? null : <CloudUploadIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      borderRadius: '8px',
                      px: 3,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      boxShadow: 'none',
                      '&:hover': {
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isUploading ? <CircularProgress size={16} color="inherit" /> : 'Upload Now'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          // View/List history interface - shown when toggleUpload is false
          <Box>
            {isFetching ? (
              <Box display="flex" justifyContent="center" py={1}>
                <CircularProgress size={24} />
              </Box>
            ) : additionalDocuments && additionalDocuments.length > 0 ? (
              <Grid container spacing={1.5}>
                {additionalDocuments.map((doc: any) => {
                  const fileName = getFileName(doc.file);
                  const isPdf = fileName.toLowerCase().endsWith('.pdf');
                  const isImage = fileName.toLowerCase().endsWith('.png') ||
                    fileName.toLowerCase().endsWith('.jpg') ||
                    fileName.toLowerCase().endsWith('.jpeg');

                  return (
                    <Grid item xs={12} sm={6} md={4} key={doc.id}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.5,
                          borderColor: '#e2e8f0',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#cbd5e1',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0, flex: 1 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '6px',
                              backgroundColor: isPdf ? '#fef2f2' : isImage ? 'transparent' : '#f1f5f9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: isPdf ? '#ef4444' : '#64748b',
                              flexShrink: 0,
                              overflow: 'hidden',
                              border: isImage ? '1px solid #e2e8f0' : 'none',
                            }}
                          >
                            {isPdf ? (
                              <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                            ) : isImage ? (
                              <img
                                src={`${imgUrl}/${doc.file}`}
                                alt="preview"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <InsertDriveFileIcon sx={{ fontSize: 18 }} />
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight="600"
                              noWrap
                              title={fileName}
                              sx={{ color: '#1e293b', fontSize: '0.8rem', mb: 0.25 }}
                            >
                              {fileName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.68rem' }}>
                              {moment(doc.created_at).format('Do MMM YYYY, h:mm A')}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setPreviewFile(doc.file);
                            setPreviewOpen(true);
                          }}
                          sx={{
                            backgroundColor: '#e0e7ff',
                            color: '#4f46e5',
                            flexShrink: 0,
                            width: 28,
                            height: 28,
                            '&:hover': {
                              backgroundColor: '#c7d2fe',
                            },
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box
                sx={{
                  py: 3,
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '8px',
                }}
              >
                <Typography color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                  No additional documents uploaded yet.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      <ModalsWrapper
        modalData={{ title: 'View document' }}
        setShowModal={setPreviewOpen}
        showModal={previewOpen}
      >
        <PdfAndImageView file={previewFile} />
      </ModalsWrapper>
    </Card>
  );
};

export default AdditionalFileUpload;
